import React, { useState, useEffect } from 'react';
import { 
  Users, Store, CreditCard, TrendingUp, 
  Calendar, Map, BarChart2, PieChart,
  Menu, ArrowUpRight, ArrowDownRight,
  Activity, DollarSign, UserPlus, Search,
  Bell, X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { NotificationPanel } from '../components/NotificationPanel';

interface DashboardStats {
  totalShopOwners: number;
  totalCustomers: number;
  totalTransactions: number;
  totalVolume: number;
  activeUsers: number;
  registrationRate: number;
}

interface TransactionMetrics {
  date: string;
  daily_amount: number;
  daily_count: number;
}

interface ReferralStats {
  username: string;
  smart_key: string;
  shop_owner_referrals: number;
  customer_referrals: number;
  total_transactions: number;
  total_volume: number;
}

interface DateRange {
  from: string;
  to: string;
}

function Dashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [transactionMetrics, setTransactionMetrics] = useState<TransactionMetrics[]>([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'referrals' | 'notifications'>('overview');
  const [referralStats, setReferralStats] = useState<ReferralStats[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      from: startOfMonth.toISOString().split('T')[0],
      to: endOfMonth.toISOString().split('T')[0]
    };
  });

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = sessionStorage.getItem('dashboardAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'referrals') {
      fetchReferralStats();
    }
  }, [activeTab, dateRange]);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'Waleed_733153303') {
      setIsAuthenticated(true);
      sessionStorage.setItem('dashboardAuth', 'true');
      fetchDashboardData();
    } else {
      setError('كلمة المرور غير صحيحة');
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_dashboard_stats');

      if (statsError) throw statsError;
      setStats(statsData);

      const { data: metricsData, error: metricsError } = await supabase
        .rpc('get_transaction_metrics');

      if (metricsError) throw metricsError;
      setTransactionMetrics(metricsData || []);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralStats = async () => {
    try {
      setLoadingReferrals(true);
      
      const { data, error } = await supabase
        .rpc('get_referral_stats', {
          date_from: `${dateRange.from}T00:00:00Z`,
          date_to: `${dateRange.to}T23:59:59Z`
        });

      if (error) throw error;
      setReferralStats(data || []);
    } catch (err) {
      console.error('Error fetching referral stats:', err);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const filteredReferrals = referralStats.filter(stat => 
    stat.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    stat.smart_key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderDateFilter = () => (
    <div className="flex items-center space-x-4 bg-white p-4 rounded-xl shadow-sm">
      <Calendar className="text-gray-400" size={20} />
      <div className="flex items-center space-x-2">
        <div>
          <label className="block text-sm text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
          />
        </div>
      </div>
    </div>
  );

  const renderOverviewContent = () => (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="text-blue-500" size={24} />
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              (stats?.registrationRate || 0) >= 0 
                ? 'bg-green-50 text-green-600' 
                : 'bg-red-50 text-red-600'
            }`}>
              {(stats?.registrationRate || 0) >= 0 ? (
                <div className="flex items-center">
                  <ArrowUpRight size={14} className="mr-1" />
                  {stats?.registrationRate}%
                </div>
              ) : (
                <div className="flex items-center">
                  <ArrowDownRight size={14} className="mr-1" />
                  {Math.abs(stats?.registrationRate || 0)}%
                </div>
              )}
            </span>
          </div>
          <h3 className="text-gray-500 text-sm mb-2">Total Users</h3>
          <p className="text-2xl font-bold">
            {(stats?.totalShopOwners || 0) + (stats?.totalCustomers || 0)}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Shop Owners: {stats?.totalShopOwners || 0}</span>
            <span>Customers: {stats?.totalCustomers || 0}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <DollarSign className="text-green-500" size={24} />
            </div>
            <span className="px-2.5 py-1 bg-green-50 text-green-600 rounded-full text-xs font-medium">
              Today: ${formatCurrency(transactionMetrics[0]?.daily_amount || 0)}
            </span>
          </div>
          <h3 className="text-gray-500 text-sm mb-2">Total Volume</h3>
          <p className="text-2xl font-bold">${formatCurrency(stats?.totalVolume || 0)}</p>
          <p className="mt-2 text-xs text-gray-500">
            {stats?.totalTransactions || 0} total transactions
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Activity className="text-purple-500" size={24} />
            </div>
            <span className="px-2.5 py-1 bg-purple-50 text-purple-600 rounded-full text-xs font-medium">
              Last 30 days
            </span>
          </div>
          <h3 className="text-gray-500 text-sm mb-2">Active Users</h3>
          <p className="text-2xl font-bold">{stats?.activeUsers || 0}</p>
          <p className="mt-2 text-xs text-gray-500">
            {((stats?.activeUsers || 0) / ((stats?.totalShopOwners || 0) + (stats?.totalCustomers || 0)) * 100).toFixed(1)}% of total users
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Calendar className="text-orange-500" size={24} />
            </div>
            <span className="px-2.5 py-1 bg-orange-50 text-orange-600 rounded-full text-xs font-medium">
              Today
            </span>
          </div>
          <h3 className="text-gray-500 text-sm mb-2">Daily Activity</h3>
          <p className="text-2xl font-bold">{transactionMetrics[0]?.daily_count || 0}</p>
          <p className="mt-2 text-xs text-gray-500">
            transactions processed today
          </p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-800">Transaction Trends</h3>
              <p className="text-sm text-gray-500">Last 30 days activity</p>
            </div>
            <BarChart2 className="text-blue-500" size={24} />
          </div>
          <div className="h-64 flex items-center justify-center text-gray-500">
            Chart will be implemented here
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-800">User Distribution</h3>
              <p className="text-sm text-gray-500">By account type</p>
            </div>
            <PieChart className="text-purple-500" size={24} />
          </div>
          <div className="h-64 flex items-center justify-center text-gray-500">
            Chart will be implemented here
          </div>
        </div>
      </div>
    </>
  );

  const renderReferralContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by username or smart key..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
        {renderDateFilter()}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">User</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Smart Key</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Shop Owner Referrals</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Customer Referrals</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Total Transactions</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Total Volume</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredReferrals.map((stat) => (
                <tr key={stat.smart_key} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <ProfileAvatar
                        username={stat.username}
                        size="sm"
                      />
                      <span className="font-medium">{stat.username}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center font-mono text-sm">
                    {stat.smart_key}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {stat.shop_owner_referrals}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {stat.customer_referrals}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {stat.total_transactions}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-medium">
                    ${formatCurrency(stat.total_volume)}
                  </td>
                </tr>
              ))}
              {filteredReferrals.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No referral data found for the selected date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">لوحة التحكم</h1>
            <p className="text-gray-600">أدخل كلمة المرور للوصول إلى لوحة التحكم</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="كلمة المرور"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            >
              دخول
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="mt-6 w-full py-2 text-gray-600 hover:text-gray-800 text-sm"
          >
            العودة إلى الصفحة الرئيسية
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
          <div className="text-red-500 mb-4">
            <Activity size={40} />
          </div>
          <h2 className="text-xl font-semibold mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => fetchDashboardData()}
            className="w-full py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        showSidebar ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Ajel Analytics
            </h1>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group ${
                activeTab === 'overview' 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-gray-600 hover:bg-blue-50'
              }`}
            >
              <Activity className="group-hover:scale-110 transition-transform" size={20} />
              <span>Overview</span>
            </button>
            <button 
              onClick={() => setActiveTab('referrals')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group ${
                activeTab === 'referrals' 
                  ? 'bg-purple-50 text-purple-600' 
                  : 'text-gray-600 hover:bg-purple-50'
              }`}
            >
              <UserPlus className="group-hover:scale-110 transition-transform" size={20} />
              <span>Referrals</span>
            </button>
            <button 
              onClick={() => setActiveTab('notifications')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group ${
                activeTab === 'notifications' 
                  ? 'bg-red-50 text-red-600' 
                  : 'text-gray-600 hover:bg-red-50'
              }`}
            >
              <Bell className="group-hover:scale-110 transition-transform" size={20} />
              <span>Notifications</span>
            </button>
          </nav>

          <div className="p-4 border-t">
            <div className="bg-blue-50 p-4 rounded-xl">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Pro Tip</h3>
              <p className="text-sm text-blue-600">
                Use the dashboard to track your business growth and make data-driven decisions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${showSidebar ? 'ml-64' : 'ml-0'} transition-margin duration-300 ease-in-out`}>
        <div className="p-8">
          {/* Toggle Sidebar Button */}
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-lg hover:bg-gray-50 transition-colors"
          >
            {showSidebar ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-800">
              {activeTab === 'overview' ? 'Analytics Overview' : 
               activeTab === 'referrals' ? 'Referral Analysis' :
               'Push Notifications'}
            </h1>
            <p className="text-gray-500">
              {activeTab === 'overview' 
                ? 'Track your business performance and growth'
                : activeTab === 'referrals'
                ? 'Monitor user referrals and their impact'
                : 'Send push notifications to all users'
              }
            </p>
          </div>

          {/* Content */}
          {activeTab === 'overview' && renderOverviewContent()}
          {activeTab === 'referrals' && renderReferralContent()}
          {activeTab === 'notifications' && (
            <div className="max-w-2xl mx-auto">
              <NotificationPanel className="bg-white rounded-2xl" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;